import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Subscription, Observable, of, forkJoin } from "rxjs";
import { tap, map, mergeMap } from "rxjs/operators";


import { BaseControlComponent } from "../../../../../../form-controls/components/base-control/base-control.component";
import { ContextInteractionService } from "../../../../../../form-controls/services/context-interaction.service";
import { DocumentManagementService } from "../../../../../../common/services/document-management.service";
import { OnBaseDocumentGroup, OnBaseDocumentType, OnBaseKeyword, OnBaseMappedKeyword, OnBaseKeywordMappingSearchSpec } from "../../../../../../common/models/onBaseIntegrationAndMapping.model";
import { SidePanelService } from "../../../../../../common/services/side-panel.service";
import { DynamicDialogService } from "../../../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../../../core/models/dialog.model";


@Component({
    selector: 'onbase-file-upload-data-config',
    templateUrl: './onbase-file-upload-data-config.component.html',
    styleUrls: ['./onbase-file-upload-data-config.component.scss'],
    standalone: false
})
export class OnBaseFileUploadDataConfigComponent extends BaseControlComponent implements OnInit, OnDestroy {
    static componentName = "FileUploadDataConfigComponent";

    automaticKeywords: string[] =
        [
            "DOGRM - Date Uploaded",
            "DOGRM - Program Area",
            "DOGRM - Confidential",
            "DOGRM - RICS Application Type",
            "DOGRM - RICS Owner ID Number",
            "DOGRM - RICS Workflow Instance",
            "DOGRM - RICS Workflow Name",
            "DOGRM - RICS Workflow Template ID"
        ];

    @Input() componentData: any = {
        config: {
            documentType: -1,
            documentGroup: -1,
            keywords: []
        }
    };

    get config(): any {
        return this.componentData.config;
    }

    get disableAdd(): boolean {
        let disable: boolean = true;
        let ctrl = this.formGroup.get("selectedKeyword")
        if (ctrl) {
            let value = ctrl.value;
            disable = ctrl.value === -1;
        }
        return disable;
    }

    get disableSave(): boolean {
        if (this.formGroup != null && this.formGroup.invalid) return true;
        return !this.keywordsLoaded;
    }

    documentGroupDocumentTypeMap: Map<string, OnBaseDocumentType[]> = new Map<string, OnBaseDocumentType[]>();
    documentGroups: OnBaseDocumentGroup[];
    documentTypeKeywordMap: Map<string, OnBaseKeyword[]> = new Map<string, OnBaseKeyword[]>();
    documentTypes: OnBaseDocumentType[];

    errorDialog: Dialog = new Dialog("An error has occurred.", [new DialogButton("OK", false, () => { return; })]);
    expressionWarning = "Keyword values will be treated as Expressions.  Be sure to escape ('value') any constant values.";
    formBuilt: boolean = false;

    get helpText(): string {
        let result = "These keywords do <span class=\"bold\">not</span> need to be mapped here: <br />";
        result += "<ul>";
        for (let k = 0; k < this.automaticKeywords.length; k++) {
            result += `<li>${this.getKeywordNameDisplay(this.automaticKeywords[k])}</li>`;
        }
        result += "</ul>";
        return result;
    }
    keywordExistsDialog: Dialog = new Dialog("Keyword has already been configured.  Do you want to remove the existing keyword configuration?.", [new DialogButton("Cancel", false, () => { return; }), new DialogButton("OK", true, () => { this.removeAndReplace(); })]);
    keywordList: OnBaseKeyword[];
    keywordsLoaded: boolean = true;

    readonly selectDocumentGroup: OnBaseDocumentGroup = { id: -1, name: "-- Select a Document Group --" };
    readonly selectDocumentType: OnBaseDocumentType = { id: -1, name: "-- Select a Document Type --" };

    get selectedGroup(): OnBaseDocumentGroup {
        let group: OnBaseDocumentGroup = null;
        if (this.componentData && this.config && this.config.documentGroup != null) {
            group = this.documentGroups.find(g => g.name === this.config.documentGroup);            
        }
        return (group != null) ? group : this.selectDocumentGroup;
    }
    requiredKeywords: OnBaseKeyword[];
    selectedKeywords: OnBaseKeyword[] = [];

    saveDialog: Dialog = new Dialog("Configuration has been recorded.  Added keywords with no configured value have been removed.  Flow Template must be saved for changes to be permanently saved.  Do you want to close the sidebar?", [new DialogButton("No", false, () => { return; }), new DialogButton("Yes", true, () => { this.confirm(); })]);

    selectAKeyword: OnBaseKeyword = { id: -1, name: "-- Select a Keyword --", dataLength: 0, dataType: "string" };

    get selectedType(): OnBaseDocumentType {
        let type: OnBaseDocumentType = null;
        if (this.componentData && this.config && this.config.documentType != null) {
            type = this.documentTypes.find(g => g.name === this.config.documentType);
        }
        return (type != null) ? type : this.selectDocumentType;
    }

    subscription: Subscription = new Subscription();

    unsavedChangesDialog: Dialog = new Dialog("You have made changes that have not yet been saved.  Are you sure you wish to cancel?", [new DialogButton("No", false, () => { return; }), new DialogButton("Yes", true, () => { this.confirm(); })])

    constructor(readonly contextService: ContextInteractionService, private builder: FormBuilder, private readonly docService: DocumentManagementService, private readonly panelService: SidePanelService, private readonly dialogService: DynamicDialogService) {        
        super(contextService);        
    }

    ngOnInit() {
        if (this.formGroup == null) this.formGroup = new FormGroup({});
        this.documentGroupDocumentTypeMap.set(this.selectDocumentType.name, [this.selectDocumentType]);
        this.documentTypeKeywordMap.set(this.selectAKeyword.name, [this.selectAKeyword]);
        this.keywordList = [this.selectAKeyword];
        const dataLoad$ = this.docService.getAllDocumentGroups().pipe(
            tap((data) => {
                this.documentGroups = data;
                this.documentGroups.unshift(this.selectDocumentGroup);
            }),
            mergeMap(() => {
                return this.loadDocumentTypesByGroup(this.selectedGroup.name);
            }),
            mergeMap(() => {
                return this.loadKeywordsByDocumentType(this.selectedType.name);
            })).subscribe(
            () => {
                this.buildForm();
            },
            (error) => {
                console.error(error);
                this.handleError(error);
            }
        );
        this.subscription.add(dataLoad$);
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    addKeyword(): void {
        let ctrl = this.formGroup.get("selectedKeyword");
        if (ctrl) {
            let word = this.keywordList.find(w => w.name === ctrl.value);
            if (word != null) {
                let index = this.selectedKeywords.findIndex(i => i.name === word.name);
                if (index === -1) {
                    let mappedWord = <OnBaseMappedKeyword>word;
                    mappedWord.isRequired = false;
                    this.selectedKeywords.push(mappedWord);
                } else {
                    this.keywordExistsDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.keywordExistsDialog);
                }
            }
        }
    }

    buildForm(): void {
        this.formGroup = this.builder.group({
            documentGroup: [this.selectedGroup.name, [Validators.required, Validators.min(0)]],
            documentType: [this.selectedType.name, [Validators.required, Validators.min(0)]],
            keywords: this.getKeywordGroup(),
            selectedKeyword: [ this.selectAKeyword.name, []]
        });
        this.mapDefinedKeywordData();
        this.formBuilt = true;
    }

    cancel(): void {
        if (this.formGroup.dirty) {
            this.unsavedChangesDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.unsavedChangesDialog);
        } else {
            this.confirm();
        }
    }

    confirm(): void {
        this.formGroup.reset();
        this.panelService.toggleSidePanel(false);
    }

    documentGroupChanged(evt: Event): void {
        let ctrl = this.formGroup.get("documentGroup");
        if (ctrl) {
            const value = ctrl.value;
            this.documentTypes = [];
            const typeCtrl = this.formGroup.get("documentType");
            if (typeCtrl) {
                typeCtrl.setValue(this.selectDocumentType.name);
            }
            let types$ = this.loadDocumentTypesByGroup(value).subscribe(
                () => { },
                (error) => {
                    console.error(error);
                    this.handleError(error);
                }
            );
            this.subscription.add(types$);
        }
    }

    documentTypeChanged(evt: Event): void {
        let ctrl = this.formGroup.get("documentType");
        if (ctrl) {
            const value = ctrl.value;
            this.keywordList = [];
            let words$ = this.loadKeywordsByDocumentType(value).subscribe(
                () => { },
                (error) => {
                    console.error(error);
                    this.handleError(error);
                }
            );
            this.subscription.add(words$);
        }
    }


    getDocumentTypesByGroup(group: string): Observable<OnBaseDocumentType[]> {
        let obs$: Observable<OnBaseDocumentType[]>;
        if (this.documentGroupDocumentTypeMap.has(group)) {
            let types = this.documentGroupDocumentTypeMap.get(group);
            obs$ = of(types);
        } else {
            obs$ = this.docService.getDocumentTypesByDocumentGroupName(group).pipe(
                tap((types: OnBaseDocumentType[]) => {
                    this.documentGroupDocumentTypeMap.set(group, types);
                })
            )
        }
        return obs$;
    }

    getKeywordGroup(): FormGroup {
        let formObj = {};
        for (let k = 0; k < this.keywordList.length; k++) {
            let word: OnBaseMappedKeyword = <OnBaseMappedKeyword>this.keywordList[k];
            const required = word.isRequired != null && word.isRequired === true && !this.isAnAutomaticallySetKeyword(word);
            formObj[word.name] = (required)
                ? ["", [Validators.required, Validators.minLength(1)]]
                : ["", [Validators.minLength(1)]];
        }
        return this.builder.group(formObj);
    }

    isAnAutomaticallySetKeyword(keyword: OnBaseKeyword) {
        let isInAutomaticallySetList = this.automaticKeywords.filter(x => x == (<OnBaseMappedKeyword>keyword).name).length > 0;
        return isInAutomaticallySetList;
    }

    getKeywordsByDocumentType(type: string): Observable<OnBaseKeyword[]> {
        let obs$: Observable<OnBaseKeyword[]>;
        if (this.documentTypeKeywordMap.has(type)) {
            let words = this.documentTypeKeywordMap.get(type);
            obs$ = of(words);
        } else {
            this.keywordsLoaded = false;
            let keywords$ = this.docService.getKeywordTypesByDocumentTypeName(type);
            let documentType = this.docService
            let mappings$ = this.docService.findKeywordMappings({ documentTypeName: type } as OnBaseKeywordMappingSearchSpec);
            obs$ = forkJoin(keywords$, mappings$).pipe(map(([words, mappings]) => {
                let result: OnBaseKeyword[] = [];
                if (words == null && mappings != null) {
                    result = mappings;
                } else if (words != null && mappings == null) {
                    result = words;
                } else {
                    for (let w = 0; w < words.length; w++) {
                        let mapIndex = mappings.findIndex(m => m.name === words[w].name);
                        if (mapIndex !== -1) {
                            result.push(mappings[mapIndex]);
                        } else {
                            result.push(words[w]);                           
                        }
                    }
                }
                this.documentTypeKeywordMap.set(type, result);                
                return result;
            }));
        }
        return obs$;
    }

    getKeywordsNameDisplay(keyword: OnBaseKeyword): string {
        return this.getKeywordNameDisplay(keyword.name);
    }

    getKeywordNameDisplay(keywordName: string): string {
        return keywordName.replace('DOGRM - ', '');
    }

    handleError(error): void {
        this.errorDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.errorDialog);
    }

    isKeywordInvalid(keyword: OnBaseKeyword): boolean {
        let group = <FormGroup>this.formGroup.get("keywords");
        let ctrl = group.get(keyword.name);
        return (ctrl) ? ctrl.invalid: false;
    }

    loadDocumentTypesByGroup(group: string): Observable<OnBaseDocumentType[]> {
        let obs$: Observable<OnBaseDocumentType[]> =
            (group === this.selectDocumentGroup.name)
                ? of([])
                : this.getDocumentTypesByGroup(group)
        return obs$.pipe(tap((types: OnBaseDocumentType[]) => {
            this.documentTypes = types;
            if (this.documentTypes.findIndex(d => d.id === this.selectDocumentType.id) === -1) {
                this.documentTypes.unshift(this.selectDocumentType);
            }
        }));
    }

    loadKeywordsByDocumentType(type: string): Observable<OnBaseKeyword[]> {
        let obs$: Observable<OnBaseKeyword[]> =
            (type === this.selectDocumentType.name)
                ? of([])
                : this.getKeywordsByDocumentType(type)
       return obs$.pipe(
            tap((words: OnBaseKeyword[]) => {
                this.keywordList = words;
                if (this.keywordList.findIndex(k => k.id === -1) === -1) {
                    this.keywordList.unshift(this.selectAKeyword);
                }
                this.requiredKeywords = words.filter(w => (<OnBaseMappedKeyword>w).isRequired && !(this.automaticKeywords.filter(x => x == (<OnBaseMappedKeyword>w).name).length > 0));
                this.formGroup.setControl("keywords", this.getKeywordGroup());
                if (this.config.keywords != null && this.config.keywords.length > 0) {
                    for (let k = 0; k < this.config.keywords.length; k++) {
                        let word = this.keywordList.find(li => li.name === this.config.keywords[k].name);
                        if (word != null) {
                            if (this.selectedKeywords == null) this.selectedKeywords = [];
                            this.selectedKeywords.push(word);
                        }
                    }
                }
                this.selectedKeywords = this.requiredKeywords.concat(this.selectedKeywords);
                this.keywordsLoaded = true;
            })
        );
    }

    mapDefinedKeywordData(): void {
        if (this.config != null && this.config.keywords != null) {
            let keywordGroup = <FormGroup>this.formGroup.get("keywords");
            if (keywordGroup != null) {
                for (let k = 0; k < this.config.keywords.length; k++) {
                    let name = this.config.keywords[k].name;
                    let wordCtrl = keywordGroup.get(name);
                    if (wordCtrl != null) {
                        wordCtrl.patchValue(this.config.keywords[k].value);
                    }
                }
            }           
        }
    }

    removeAndReplace() {
        let ctrl = this.formGroup.get("selectedKeyword");
        if (ctrl != null) {
            let keywordGroup = <FormGroup>this.formGroup.get("keywords");
            if (keywordGroup != null) {
                let keyCtrl = keywordGroup.get(ctrl.value.toString());
                if (keyCtrl != null) keyCtrl.patchValue("");
            }
        }
    }

    removeExisting(index: number): void {
        if (index !== -1) this.selectedKeywords.splice(index, 1);
    }

    removeKeyword(keyword: OnBaseKeyword): void {
        let index = this.selectedKeywords.findIndex(k => k.name === keyword.name);
        this.removeExisting(index);
        let keywordGroup = <FormGroup>this.formGroup.get("keywords");
        if (keywordGroup != null) {
            let keyCtrl = keywordGroup.get(keyword.name);
            if (keyCtrl != null) keyCtrl.patchValue("");
        }
    }

    saveChanges(): void {
        let group = this.formGroup.get("documentGroup");
        const newGroup = this.documentGroups.find(g => g.name === group.value);
        this.config.documentGroup = newGroup.name;
        let type = this.formGroup.get("documentType");
        const newType = this.documentTypes.find(t => t.name === type.value);
        this.config.documentType = newType.name;
        let keywords = <FormGroup>this.formGroup.get("keywords");
        let removeList: OnBaseKeyword[] = [];
        this.config.keywords = [];
        for (let w = 0; w < this.selectedKeywords.length; w++) {            
            let word = this.selectedKeywords[w];
            let ctrl = keywords.get(word.name);
            if (ctrl) {
                let value = ctrl.value;
                
                if (value != null && value.length > 0) {
                    this.config.keywords.push({ id: word.id, name: word.name, dataType: word.dataType, dataLength: word.dataLength, value: value });
                } else {
                    removeList.push(word);
                }
                
            }
        }
        removeList.forEach(r => {
            this.removeKeyword(r);
        });
        this.saveDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.saveDialog);
    }
}
